import React, { useEffect } from "react";
import { Styled } from "../../styles";
import { Button, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { HelpIcon, Input } from "@dippi/ui";
import { Forms } from "@/types/form";
import { yupResolver } from "@hookform/resolvers/yup";
import schemas from "@/utils/schemas";
import constant from "@/utils/constant";
import { useSubmit } from "./hooks";
import { useUser } from "@/context/user";

interface Props {
    setStep: React.Dispatch<React.SetStateAction<number>>;
    setState: React.Dispatch<
        React.SetStateAction<{
            step1: Forms["OrganizationStep1"] | null;
            step2: Forms["OrganizationStep2"] | null;
        }>
    >;
    state: { step1: Forms["OrganizationStep1"] | null; step2: Forms["OrganizationStep2"] | null };
    onBack: React.MutableRefObject<(() => void) | undefined>;
}

const Form: React.FC<Props> = ({ setStep, setState, state, onBack }) => {
    const { control, handleSubmit, getValues } = useForm<Forms["OrganizationStep2"]>({
        resolver: yupResolver(schemas.organization.step2),
        defaultValues: state.step2 ?? {
            email: useUser.getState().email ?? "",
            phone: "",
            city: "",
            street: "",
            zipCode: "",
        },
    });

    const { mutateAsync: onSubmit } = useSubmit();

    useEffect(() => {
        onBack.current = () => {
            setState(state => ({ ...state, step2: getValues() }));
            setStep(step => step - 1);
        };
    }, []);

    return (
        <Styled.Form
            onSubmit={handleSubmit(values => {
                setState(state => {
                    const newState = { ...state, step2: values };
                    onSubmit(newState);
                    return newState;
                });
            })}
        >
            <Grid container rowGap={constant.GRID_SPACING}>
                <Grid item xs={12}>
                    <Typography variant="h3" textAlign="center">
                        Create your organization
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Input
                        name="email"
                        control={control}
                        label="Organization email"
                        type="email"
                        InputProps={{
                            endAdornment: (
                                <HelpIcon
                                    key="organization-email-help"
                                    title="Organization email will be used for invoicing purposes. Change it if you'd like to use different email address."
                                />
                            ),
                        }}
                        tabIndex={1}
                        testId="organization-email-input"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Input
                        name="phone"
                        control={control}
                        label="Mobile number"
                        type="tel"
                        tabIndex={2}
                        testId="organization-phone-input"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Input
                        name="street"
                        control={control}
                        label="Street"
                        tabIndex={3}
                        testId="organization-street-input"
                    />
                </Grid>
                <Grid item container xs={12} display="flex" justifyContent="space-between">
                    <Grid item xs={12} lg={5.5} xl={5.5}>
                        <Input
                            name="city"
                            control={control}
                            label="City"
                            tabIndex={4}
                            testId="organization-city-input"
                        />
                    </Grid>
                    <Grid item xs={12} lg={5.5} xl={5.5}>
                        <Input
                            name="zipCode"
                            control={control}
                            label="Post code"
                            tabIndex={5}
                            testId="organization-zip-code-input"
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Button
                        type="submit"
                        fullWidth={true}
                        tabIndex={6}
                        data-testid="organization-submit-button"
                    >
                        Create
                    </Button>
                </Grid>
            </Grid>
        </Styled.Form>
    );
};

export default Form;
